import { fromEvent, merge, Observable } from 'rxjs';
import { filter, first, mergeMap, share, tap } from 'rxjs/operators';
import { hasCodeAncestor } from '../../helpers/dom/code';
import {
  codeBlockCursorByDeletingContents,
  isCodeBlockElement,
  isCodeBlockTrailingContent,
} from '../../helpers/dom/codeBlock';
import { isPhrasingContent } from '../../helpers/dom/content';
import {
  findAncestor,
  findLastTextLikeNode,
  findNextNonWhitespaceSibling,
  findPreviousNonWhitespaceSibling,
} from '../../helpers/dom/node';
import { createEmptyParagraph } from '../../helpers/dom/paragraph';
import { createPointWithStartOfRange } from '../../helpers/dom/point';
import { InteractionAction, InteractionHandler, InteractionTrigger } from '../../interactionEvent';
import { Service } from '../base/service';
import { UndoService } from '../text/undoService';
import { ElementSelectionService } from './elementSelectionService';
import { createElementEffectService } from './elementService';

export type ElementCodeBlockServiceOptions = {
  onInteraction?: InteractionHandler;
};

export function createElementCodeBlockService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  undoService: UndoService,
  options: ElementCodeBlockServiceOptions = {},
): Service {
  return createElementEffectService(element$, element =>
    merge(
      insertCodeBlockAsYouTypeEffect(element, selectionService, undoService, options.onInteraction),
      codeBlockLineBreakEffect(element, selectionService),
      moveCursorBeforeCodeBlockEffect(element, selectionService),
      moveCursorAfterCodeBlockEffect(element, selectionService),
    ).pipe(share()),
  );
}

function insertCodeBlockAsYouTypeEffect(
  element: Element,
  selectionService: ElementSelectionService,
  undoService: UndoService,
  onInteraction?: InteractionHandler,
) {
  return fromEvent<KeyboardEvent>(element, 'keydown').pipe(
    // Listen for the ` or ~ key being pressed, to establish typing intent.
    filter(event => event.key === '`' || event.key === '~'),
    // Listen for the next text/selection change that results from that key press.
    mergeMap(() => selectionService.changes$.pipe(first())),
    tap(() => {
      // Do we have a cursor?
      const { container, selection } = selectionService;
      if (!container || !selection || !selection.isCollapsed) {
        return;
      }

      // Are we already in inline code or a code block?
      if (hasCodeAncestor(selection.focusNode, container)) {
        return;
      }

      // Find the current block container, which we consider to be the current line.
      const lineElement = findAncestor(selection.focusNode, node => !isPhrasingContent(node), container);
      if (!lineElement) {
        return;
      }

      // Check if the line matches a code block fence.
      const lineText = lineElement.textContent || '';
      if (!/^\s{0,3}(```|~~~)$/.test(lineText)) {
        return;
      }

      const { ownerDocument: document, parentNode } = lineElement;
      if (!document || !parentNode) {
        return;
      }

      // Allow the user to undo this.
      undoService.takeAndCommitSnapshot();

      // Replace the block container with a preformatted text block.
      const codeBlockElement = document.createElement('pre');
      const codeElement = document.createElement('code');
      const fillerElement = document.createElement('br');
      codeElement.appendChild(fillerElement);
      codeBlockElement.appendChild(codeElement);
      parentNode.replaceChild(codeBlockElement, lineElement);
      selectionService.moveToOffsetInContainer(codeElement, 0);

      if (onInteraction) {
        onInteraction({
          trigger: InteractionTrigger.KEYBOARD_TYPING,
          action: InteractionAction.SET_CODE_BLOCK,
        });
      }
    }),
  );
}

function codeBlockLineBreakEffect(element: Element, selectionService: ElementSelectionService) {
  return fromEvent<KeyboardEvent>(element, 'keydown').pipe(
    filter(event => event.key === 'Enter'),
    tap(event => {
      const { ownerDocument: document } = element;
      if (!document) {
        return;
      }

      const range = selectionService.selection?.range;
      if (!range) {
        return;
      }

      const codeBlockCursor = codeBlockCursorByDeletingContents(element, range);
      if (!codeBlockCursor) {
        return;
      }

      // We got this.
      event.preventDefault();

      const { codeBlockElement, textNode, cursorOffset } = codeBlockCursor;
      const text = textNode.data;
      const textBeforeLineBreak = text.slice(0, cursorOffset);
      const textAfterLineBreak = text.slice(cursorOffset);
      const isLineBreakAtEndOfCodeBlock =
        textNode === findLastTextLikeNode(codeBlockElement) && cursorOffset === text.length;

      const newText = `${textBeforeLineBreak}\n${textAfterLineBreak}`;
      textNode.data = newText;

      // If we're at the very end of the `pre-wrap` container, we need filler since our line break will be collapsed
      // otherwise.
      if (isLineBreakAtEndOfCodeBlock && textNode.parentNode) {
        // Markdown usually renders an extra line break, but a <br> works better for cursor movement.
        const fillerElement = document.createElement('br');
        textNode.parentNode.appendChild(fillerElement);
      }

      selectionService.moveToOffsetInContainer(textNode, cursorOffset + 1);
    }),
  );
}

function moveCursorBeforeCodeBlockEffect(element: Element, selectionService: ElementSelectionService) {
  return fromEvent<KeyboardEvent>(element, 'keydown').pipe(
    filter(event => event.key === 'ArrowUp'),
    tap(event => {
      const { ownerDocument: document } = element;
      if (!document) {
        return;
      }

      // Check we have cursor (a collapsed selection).
      const range = selectionService.selection?.range;
      if (!range || !range.collapsed) {
        return;
      }

      // Check the cursor is in a code bock.
      const codeBlockElement = findAncestor(range.startContainer, isCodeBlockElement, element);
      if (!codeBlockElement) {
        return;
      }

      // Check the cursor is at the start of the code block.
      const cursorPoint = createPointWithStartOfRange(range);
      const isAtStartOfCodeBlock = cursorPoint.isAtStartOfNode(
        codeBlockElement,
        node => !findPreviousNonWhitespaceSibling(node),
        element,
      );
      if (!isAtStartOfCodeBlock) {
        return;
      }

      // Check the cursor is also at the start of the `contenteditable`.
      const isAtStartOfEditor = cursorPoint.isAtStartOfNode(
        element,
        node => !findPreviousNonWhitespaceSibling(node),
        element,
      );
      if (!isAtStartOfEditor) {
        return;
      }

      // We got this.
      event.preventDefault();

      // Insert an empty line before the code block.
      const lineElement = createEmptyParagraph(document);
      element.insertBefore(lineElement, element.firstChild);

      // Move the cursor into the empty line.
      selectionService.moveToOffsetInContainer(lineElement, 0);
    }),
  );
}

function moveCursorAfterCodeBlockEffect(element: Element, selectionService: ElementSelectionService) {
  return fromEvent<KeyboardEvent>(element, 'keydown').pipe(
    filter(event => event.key === 'ArrowDown'),
    tap(event => {
      const { ownerDocument: document } = element;
      if (!document) {
        return;
      }

      // Check we have a cursor (a collapsed selection).
      const range = selectionService.selection?.range;
      if (!range || !range.collapsed) {
        return;
      }

      // Check the cursor is in a code bock.
      const codeBlockElement = findAncestor(range.startContainer, isCodeBlockElement, element);
      if (!codeBlockElement) {
        return;
      }

      // Check the cursor is at the end of the code block, ignoring any filler.
      const cursorPoint = createPointWithStartOfRange(range);
      const isAtEndOfCodeBlock = cursorPoint
        .moveForwardWhile(isCodeBlockTrailingContent, codeBlockElement)
        .isAtEndOfNode(codeBlockElement, node => !findNextNonWhitespaceSibling(node), element);
      if (!isAtEndOfCodeBlock) {
        return;
      }

      // Check the cursor is also at the end of the `contenteditable`, ignoring any filler.
      const isAtEndOfEditor = cursorPoint
        .moveForwardWhile(isCodeBlockTrailingContent, element)
        .isAtEndOfNode(element, node => !findNextNonWhitespaceSibling(node), element);
      if (!isAtEndOfEditor) {
        return;
      }

      // We got this.
      event.preventDefault();

      // Insert an empty line after the code block.
      const lineElement = createEmptyParagraph(document);
      element.appendChild(lineElement);

      // Move the cursor into the empty line.
      selectionService.moveToOffsetInContainer(lineElement, 0);
    }),
  );
}
